<script lang="ts" setup>
import _ from 'lodash'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { hasPermission, RoleEnum } from '@/misc/permissions/permissionsHelper'
import { SmrStaySuggestionItem, SuggestionJustification } from '@/misc/types'
import {
  getSuggestedDependencies,
  MAX_DEPENDENCY_VALUE,
  MIN_DEPENDENCY_VALUE,
} from '@/stay-displayer/smr/rhs/dependency/suggestedDependencyHelper'
import RhsSuggestedDependencyItem from '@/stay-displayer/smr/rhs/RhsSuggestedDependencyItem.vue'
import { getWeekNbText } from '@/stay-displayer/smr/utils'
import { useStore } from '@/store'
import {
  DependenciesLabels,
  DependenciesSections,
  DependenciesSectionsLabels,
  DependenciesType,
  Rhs,
  RhsDashboardClass,
  RhsDashboardTitle,
  RhsDashboardType
} from '@/store/modules/stay/smr/types'
import { StayHealthData } from '@/store/modules/stay/types'

import { capitalize } from '../../../../../../commons/typescript-commons/src/misc/string-utils'

const store = useStore()
const { t } = useI18n()

const props = withDefaults(defineProps<{
  currentRhs: Rhs
  previousRhs?: Rhs | null
  healthData: StayHealthData
  suggestionJustifications?: Record<string, SuggestionJustification> | null
  // Optional: fetched separately from the main stay payload and merged in once it resolves.
  staySuggestions?: SmrStaySuggestionItem[]
}>(), {
  previousRhs: null,
  suggestionJustifications: null,
  staySuggestions: () => [],
})

const DISPATCH_DELAY = 500

const currentDependenciesValues = ref(getPropsValues())
function getPropsValues() {
  return {
    [DependenciesType.BEHAVIOR]: props.currentRhs.currentDepBehaviour,
    [DependenciesType.CONTINENCE]: props.currentRhs.currentDepContinence,
    [DependenciesType.DRESSING]: props.currentRhs.currentDepDressing,
    [DependenciesType.FEEDING]: props.currentRhs.currentDepFeeding,
    [DependenciesType.MOVING]: props.currentRhs.currentDepMoving,
    [DependenciesType.SOCIAL]: props.currentRhs.currentDepSocial,
  }
}
watch(() => props.currentRhs, () => {
  currentDependenciesValues.value = getPropsValues()
})
const pendingDependencyTypes = ref({
  [DependenciesType.BEHAVIOR]: false,
  [DependenciesType.CONTINENCE]: false,
  [DependenciesType.DRESSING]: false,
  [DependenciesType.FEEDING]: false,
  [DependenciesType.MOVING]: false,
  [DependenciesType.SOCIAL]: false,
})

const hasPreviousRhs = computed(() => {
  return props.previousRhs !== null
})

const canEditDependencies = computed(() => (hasPermission(RoleEnum.ROLE_STAY_WRITER, store.state.login.userRole)))
const dependenciesSums = computed(() => {
  const sums = {
    previousRhs:{},
    currentRhs:{}
  }

  for(const [sectionKey] of Object.entries(DependenciesLabels)) {
    sums.previousRhs[sectionKey] = 0
    sums.currentRhs[sectionKey] = 0

    for(const [dependencyKey] of Object.entries(DependenciesLabels[sectionKey])) {
      const depProp = getCurrentDepProp(dependencyKey)
      sums.previousRhs[sectionKey] += hasPreviousRhs.value ? props.previousRhs[depProp] : 0
      sums.currentRhs[sectionKey] += props.currentRhs[depProp]
    }
  }

  return sums
})

function changeDependencyValue(dependencyType: DependenciesType, valueChange: number) {
  const newDepValue = (currentDependenciesValues.value[dependencyType] ?? 0) + valueChange
  if (newDepValue > MAX_DEPENDENCY_VALUE || newDepValue < MIN_DEPENDENCY_VALUE || newDepValue === 0) {
    return
  }
  currentDependenciesValues.value[dependencyType] = newDepValue
  pendingDependencyTypes.value[dependencyType] = true
  storeDependencyValue()
}

const storeDependencyValue = _.debounce(() => {
  for (const dependencyType of Object.values(DependenciesType)) {
    const depProp = getCurrentDepProp(dependencyType)
    if (currentDependenciesValues.value[dependencyType] !== props.currentRhs[depProp]) {
      store.dispatch('smrStay/updateRhsDependency', {
        rhsId: props.currentRhs.id,
        dependencyType,
        value: currentDependenciesValues.value[dependencyType]
      }).then((response) => {
        // restore value from initial value if an error occured
        if (response && response.rawError) {
          currentDependenciesValues.value[dependencyType] = props.currentRhs[depProp]
        }
      }).finally(() => {
        pendingDependencyTypes.value[dependencyType] = false
      })
    }
  }
}, DISPATCH_DELAY)

const pendingDependencyTotals = computed(() => {
  const loadingSections = {
    [DependenciesSections.COGNITIVE]: false,
    [DependenciesSections.PHYSICAL]: false,
  }
  for(const [sectionKey] of Object.entries(DependenciesLabels)) {
    for (const dependencyType of Object.keys(DependenciesLabels[sectionKey])) {
      if (pendingDependencyTypes.value[dependencyType]) {
        loadingSections[sectionKey] = true
      }
    }
  }
  return loadingSections
})

function arrowButtonStyle(depType, limitValue) {
  return {
    opacity: (props.currentRhs[getCurrentDepProp(depType)] ?? MIN_DEPENDENCY_VALUE) === limitValue ? 0.3 : 1
  }
}

const suggestedDependencies = computed(() => getSuggestedDependencies(props.staySuggestions))

const suggestedDependenciesLength = computed(() => {
  return Object.values(suggestedDependencies.value.values).filter((value) => value !== null).length
})

function getCurrentDepProp(dependencyType: string): string {
  return `current${capitalize(dependencyType)}`
}
</script>

<template>
  <div class="col-lg-6 col-md-12 p-0">
    <div
      class="dashboard"
      :class="RhsDashboardClass[RhsDashboardType.RHS_DASHBOARD_DEPENDENCIES]"
    >
      <div class="row g-0 aligned-row">
        <strong class="col-6">{{ RhsDashboardTitle[RhsDashboardType.RHS_DASHBOARD_DEPENDENCIES] }}</strong>
        <small class="col-3 text-end">{{ hasPreviousRhs ? getWeekNbText(previousRhs) : '' }}</small>
        <small class="col-3 fw-bold text-end">{{ getWeekNbText(currentRhs) }}</small>
      </div>
      <template
        v-for="section in DependenciesSections"
        :key="section"
      >
        <div class="row g-0 aligned-row dependency-section-title">
          <div class="col-6 mt-3">
            <small class="fw-bold text-uppercase">{{ DependenciesSectionsLabels[section] }}</small>
          </div>
          <div class="col-3 mt-3 fw-bold text-end">
            {{ hasPreviousRhs ? dependenciesSums.previousRhs[section] : '' }}
          </div>
          <div class="col-3 mt-3 fw-bold text-end">
            <div
              v-if="canEditDependencies"
              class="loader loader-sm loader-center m-0 ms-3"
              :style="{opacity:pendingDependencyTotals[section] ? 1 : 0}"
            />
            <span
              :style="{ opacity: pendingDependencyTotals[section] ? '0.5' : '1' }"
            >
              {{ dependenciesSums.currentRhs[section] }}
            </span>
          </div>
        </div>
        <div
          v-for="(depLabel, depType) in DependenciesLabels[section]"
          :key="depType"
          class="row g-0 hoverable-item"
        >
          <div
            :id="depType+'-dependency'"
            class="col-6"
          >
            {{ depLabel }}
          </div>
          <div class="col-3 d-flex justify-content-end">
            {{ hasPreviousRhs ? previousRhs[getCurrentDepProp(depType)] : '' }}
          </div>
          <div class="col-3 d-flex justify-content-end position-relative fw-bold">
            <div
              v-if="canEditDependencies"
              class="loader loader-sm loader-center m-0 ms-3 position-absolute"
              style="width: 10px;height: 10px;right: 30px;top: 6px;"
              :style="{opacity:pendingDependencyTypes[depType] ? 1 : 0}"
            />
            <div
              v-if="canEditDependencies"
              class="d-none flex-column me-2 dep-arrows position-absolute"
              style="top: -1px;right: 5px;"
            >
              <span
                :id="depType + '-arrow-up'"
                class="dep-button"
                :style="arrowButtonStyle(depType, MAX_DEPENDENCY_VALUE)"
                @click="changeDependencyValue(depType, +1)"
              >▲</span>
              <span
                :id="depType + '-arrow-down'"
                class="dep-button"
                :style="arrowButtonStyle(depType, MIN_DEPENDENCY_VALUE)"
                @click="changeDependencyValue(depType, -1)"
              >▼</span>
            </div>
            <span
              :id="depType+'-level'"
              :style="{opacity: pendingDependencyTypes[depType] ? 0.5 : 1}"
            >
              {{ currentDependenciesValues[depType] }}
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>

  <div class="col-lg col-md-12 p-0">
    <section class="dashboard suggested-dependencies">
      <div
        :class="{'dashboard-title': !suggestedDependenciesLength}"
        class="aligned-row"
      >
        <small>{{ t('stayDisplayer.smr.dependency', 2) }} - {{ t('stayDisplayer.sancareSuggestion', suggestedDependenciesLength) }}</small>
      </div>
      <template v-if="suggestedDependenciesLength">
        <template
          v-for="(section, sectionLabel) in DependenciesSections"
          :key="section"
        >
          <rhs-suggested-dependency-item
            :dependency-type="section"
            :dependency-value="suggestedDependencies.values[section]"
            :searches="suggestedDependencies.searches[section]"
            :health-data="healthData"
            :suggestion-justifications="suggestionJustifications"
          />
          <div
            v-for="(depLabel, depType) in DependenciesLabels[section]"
            :key="depType"
            class="row g-0"
          >
            <div class="col-12">
              <rhs-suggested-dependency-item
                :dependency-type="depType"
                :dependency-value="suggestedDependencies.values[depType]"
                :searches="suggestedDependencies.searches[depType]"
                :health-data="healthData"
                :suggestion-justifications="suggestionJustifications"
              />
            </div>
          </div>
        </template>
      </template>
      <small
        v-else
        class="aligned-row fst-italic"
      >
        {{ t('stayDisplayer.smr.rhs.noSuggestedDependencies') }}
      </small>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.dashboard-weight,
.suggested-dependencies {
  .dependency-section-title {
    margin-bottom: 5px;
    padding-bottom: 5px;
    border-bottom: 1px solid;
  }

  .loader {
    position: absolute;
    width: 10px;
    height: 10px;
    right: 30px;
    top: 6px;
  }

  .row {
    &:hover .dep-arrows {
      display: flex !important;
    }

    .dep-arrows .dep-button {
      cursor: pointer;
      user-select: none;
      font-size: 10px;
      line-height: 10px;
      padding: 1px;
    }
  }
}

.dependency-searches {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60px;
}

.no-suggestion {
  opacity: .5;
}
</style>
